import { createFileRoute } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { PartyDetail } from "@/components/PartyDetail";
import {
  apInvoicesListQueryOptions,
  paymentsListQueryOptions,
  purchaseOrdersListQueryOptions,
  myEntitiesQueryOptions,
} from "@/lib/queries";
import type { APInvoice, Payment, PurchaseOrder } from "@/lib/types";

export const Route = createFileRoute("/_auth/vendors/$vendorId/")({
  component: VendorDetailIndex,
});

function kpiCard(label: string, value: string, sub?: string, color?: string) {
  return (
    <div
      key={label}
      className="bg-white dark:bg-gray-900 border border-slate-200 dark:border-gray-800 rounded-lg px-4 py-3 min-w-[140px]"
    >
      <div className="text-xs text-slate-500 dark:text-slate-400 mb-1">{label}</div>
      <div className={`text-lg font-semibold ${color ?? "text-slate-900 dark:text-slate-100"}`}>
        {value}
      </div>
      {sub && <div className="text-xs text-slate-400 dark:text-slate-500 mt-0.5">{sub}</div>}
    </div>
  );
}

function fmt(n: number) {
  if (Math.abs(n) >= 1_000_000) return `$${(n / 1_000_000).toFixed(1)}M`;
  if (Math.abs(n) >= 1_000) return `$${(n / 1_000).toFixed(0)}K`;
  return `$${n.toFixed(0)}`;
}

function VendorDetailIndex() {
  const { vendorId } = Route.useParams();

  const entitiesQuery = useQuery(myEntitiesQueryOptions());
  const currentEntity = entitiesQuery.data?.find((e) => e.is_current);
  const constructionEnabled = currentEntity?.construction_enabled ?? true;

  const apQ = useQuery(apInvoicesListQueryOptions({ vendor_id: vendorId }));
  const paymentsQ = useQuery(
    paymentsListQueryOptions({ direction: "outgoing", party_id: vendorId }),
  );
  const posQ = useQuery({
    ...purchaseOrdersListQueryOptions({ vendor_id: vendorId }),
    enabled: constructionEnabled,
  });

  const apInvoices: APInvoice[] = apQ.data ?? [];
  const payments: Payment[] = paymentsQ.data ?? [];
  const pos: PurchaseOrder[] = posQ.data ?? [];

  // KPI calculations
  const openAP = apInvoices
    .filter((i) => i.status === "open" || i.status === "partial")
    .reduce((s, i) => s + parseFloat(i.total) - parseFloat(i.memo_total ?? "0") - parseFloat(i.paid_amount ?? "0"), 0);

  const openPOs = pos
    .filter((p) => ["draft", "issued", "approved"].includes(p.status))
    .reduce((s, p) => s + parseFloat(String(p.original_amount ?? 0)), 0);

  // Average payment days: days between invoice date and payment date
  const paidInvoices = apInvoices.filter(
    (i) => i.status === "paid" && i.invoice_date,
  );
  const avgPayDays =
    paidInvoices.length === 0
      ? null
      : Math.round(
          paidInvoices.reduce((sum, inv) => {
            const due = new Date(inv.due_date ?? inv.invoice_date).getTime();
            const paid =
              payments
                .filter((p) => p.applications?.some((a) => a.ap_invoice_id === inv.id))
                .map((p) => new Date(p.payment_date).getTime())
                .sort()[0] ?? due;
            const diff = (paid - due) / 86_400_000; // days
            return sum + diff;
          }, 0) / paidInvoices.length,
        );

  const totalPaid = payments.reduce(
    (s, p) => s + parseFloat(String(p.amount)),
    0,
  );

  const isLoading = apQ.isLoading || paymentsQ.isLoading;

  return (
    <div>
      {/* KPI Strip */}
      <div className="px-6 py-3 border-b border-slate-100 dark:border-gray-800 bg-slate-50 dark:bg-gray-950">
        {isLoading ? (
          <div className="text-xs text-slate-400 dark:text-slate-500">Loading KPIs…</div>
        ) : (
          <div className="flex gap-3 overflow-x-auto pb-1">
            {kpiCard(
              "Open AP",
              fmt(openAP),
              `${apInvoices.filter((i) => i.status === "open" || i.status === "partial").length} invoice(s)`,
              openAP > 0 ? "text-amber-600 dark:text-amber-400" : undefined,
            )}
            {kpiCard("Total Paid (all time)", fmt(totalPaid), `${payments.length} payment(s)`)}
            {avgPayDays !== null &&
              kpiCard(
                "Avg Pay Days vs Due",
                `${avgPayDays > 0 ? "+" : ""}${avgPayDays}d`,
                avgPayDays > 5
                  ? "⚠ paying late"
                  : avgPayDays < -5
                    ? "✓ paying early"
                    : "on time",
                avgPayDays > 5
                  ? "text-red-600 dark:text-red-400"
                  : avgPayDays < -5
                    ? "text-green-600 dark:text-green-400"
                    : undefined,
              )}
            {constructionEnabled &&
              kpiCard(
                "Open POs",
                fmt(openPOs),
                `${pos.filter((p) => ["draft", "issued", "approved"].includes(p.status)).length} PO(s)`,
                openPOs > 0 ? "text-blue-700 dark:text-blue-400" : undefined,
              )}
            {constructionEnabled &&
              kpiCard(
                "Lifetime PO Value",
                fmt(pos.reduce((s, p) => s + parseFloat(String(p.original_amount ?? 0)), 0)),
                `${pos.length} PO(s) total`,
              )}
          </div>
        )}
      </div>

      {/* Full detail panel */}
      <PartyDetail partyId={vendorId} partyType="vendor" />
    </div>
  );
}
